import { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateDMY } from '../utils/dateFormat';
import { enqueue, flushQueue, queueCount, onQueueChange, isNetworkError } from '../utils/offlineQueue';

/**
 * Attendance — mobile-first Check-In / Check-Out screen for field staff.
 *
 * Captures GPS coordinates + reverse-geocoded address (best-effort) and a
 * mandatory reason for using the app instead of the office biometric machine.
 * App punches start in `approval_status='pending'` — an admin or assigned
 * approver must approve them before they count as Present on the report.
 */
const todayStr = () => new Date().toISOString().slice(0, 10);

const Attendance = () => {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [coords, setCoords] = useState({ latitude: null, longitude: null, accuracy: null });
  const [address, setAddress] = useState('');
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [toast, setToast] = useState(null);
  const [pendingSync, setPendingSync] = useState(0);

  // Subscribe to offline-queue size so the UI shows a sync badge.
  useEffect(() => {
    queueCount().then(setPendingSync);
    const off = onQueueChange(setPendingSync);
    return off;
  }, []);

  const loadHistory = async () => {
    try {
      const res = await api.get('/attendance/my-punches?days=14');
      setHistory(res.data?.items || []);
    } catch (e) {
      console.error('punch history', e);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const captureLocation = () => {
    if (!('geolocation' in navigator)) {
      setLocationError('Your browser does not expose GPS — please open this page on a phone.');
      return;
    }
    setLocating(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setCoords({ latitude, longitude, accuracy });
        // Reverse-geocode via OpenStreetMap Nominatim (no key needed, free tier).
        // Best-effort — failures just leave the address blank.
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { Accept: 'application/json' } }
          );
          const j = await r.json();
          setAddress(j.display_name || '');
        } catch (e) {
          setAddress('');
        }
        setLocating(false);
      },
      (err) => {
        setLocationError(err.message || 'Could not read GPS. Please grant permission and retry.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  useEffect(() => { captureLocation(); /* run once on mount */ }, []); // eslint-disable-line

  const submit = async (action) => {
    if (reason.trim().length < 3) {
      setToast({ kind: 'error', msg: 'Please type a reason (at least 3 characters).' });
      return;
    }
    if (coords.latitude == null) {
      setToast({ kind: 'error', msg: 'GPS location not captured yet. Tap "Refresh location" and wait.' });
      return;
    }
    setBusy(true);
    const payload = {
      action,
      reason: reason.trim(),
      latitude: coords.latitude,
      longitude: coords.longitude,
      address: address || null,
    };
    try {
      await api.post('/attendance/punch', payload);
      setToast({ kind: 'success', msg: action === 'in' ? '✅ Checked in — awaiting admin approval.' : '✅ Checked out — awaiting admin approval.' });
      setReason('');
      await loadHistory();
    } catch (e) {
      // Network failure → queue locally so the user can finish their
      // check-in / check-out even with no signal. Server validation
      // errors (4xx with a body) are surfaced normally.
      if (isNetworkError(e)) {
        await enqueue('attendance.punch', payload);
        setToast({
          kind: 'success',
          msg: action === 'in'
            ? '📶 No signal — check-in saved offline. Will sync automatically when you reconnect.'
            : '📶 No signal — check-out saved offline. Will sync automatically when you reconnect.',
        });
        setReason('');
      } else {
        setToast({ kind: 'error', msg: e?.response?.data?.detail || 'Could not save punch. Try again.' });
      }
    } finally {
      setBusy(false);
    }
  };

  const today = todayStr();
  const todayPunches = history.filter((p) => p.punch_date === today);
  const lastIn = todayPunches.find((p) => p.action === 'in');
  const lastOut = todayPunches.find((p) => p.action === 'out');

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5" data-testid="attendance-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">🕒 Attendance</h1>
        <div className="flex items-center gap-2">
          {pendingSync > 0 ? (
            <button
              type="button"
              onClick={() => flushQueue()}
              className="text-[11px] px-2 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 font-semibold hover:bg-amber-200 active:scale-95 transition"
              title="Punches queued offline. Tap to retry now."
              data-testid="attendance-sync-pill"
            >
              ⏳ {pendingSync} pending sync
            </button>
          ) : (
            <span
              className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700"
              data-testid="attendance-sync-pill"
            >
              ✅ All synced
            </span>
          )}
          <span className="text-xs text-slate-500">{formatDateDMY(today)}</span>
        </div>
      </div>
      <p className="text-sm text-slate-600">
        Use this screen to record your check-in / check-out when you cannot reach the office biometric machine. Your manager will see the GPS pin and the reason you provided.
      </p>

      {/* Today's status pill */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border p-3 bg-emerald-50 border-emerald-200" data-testid="today-in-pill">
          <div className="text-xs text-emerald-700 uppercase tracking-wide font-semibold">Today's Check-In</div>
          <div className="text-sm text-emerald-900 mt-1 font-semibold">
            {lastIn ? new Date(lastIn.punch_time).toLocaleTimeString() : '—'}
          </div>
          {lastIn && (
            <div className="text-[11px] text-emerald-800 mt-1 truncate" title={lastIn.address || ''}>
              {lastIn.address || `${lastIn.latitude?.toFixed(5)}, ${lastIn.longitude?.toFixed(5)}`}
            </div>
          )}
        </div>
        <div className="rounded-lg border p-3 bg-amber-50 border-amber-200" data-testid="today-out-pill">
          <div className="text-xs text-amber-700 uppercase tracking-wide font-semibold">Today's Check-Out</div>
          <div className="text-sm text-amber-900 mt-1 font-semibold">
            {lastOut ? new Date(lastOut.punch_time).toLocaleTimeString() : '—'}
          </div>
          {lastOut && (
            <div className="text-[11px] text-amber-800 mt-1 truncate" title={lastOut.address || ''}>
              {lastOut.address || `${lastOut.latitude?.toFixed(5)}, ${lastOut.longitude?.toFixed(5)}`}
            </div>
          )}
        </div>
      </div>

      {/* GPS panel */}
      <div className="rounded-lg border border-slate-200 p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">📍 Your Location</div>
          <button
            onClick={captureLocation}
            disabled={locating}
            className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="refresh-location-btn"
          >
            {locating ? 'Locating…' : '↻ Refresh location'}
          </button>
        </div>
        {coords.latitude != null ? (
          <>
            <div className="text-xs text-slate-600 font-mono">
              {coords.latitude.toFixed(6)}, {coords.longitude.toFixed(6)}
              {coords.accuracy ? <span className="text-slate-400"> · ±{Math.round(coords.accuracy)}m</span> : null}
            </div>
            {address && <div className="text-xs text-slate-700" data-testid="gps-address">{address}</div>}
          </>
        ) : (
          <div className="text-xs text-slate-500">{locationError || 'Capturing GPS…'}</div>
        )}
      </div>

      {/* Reason input + Action buttons */}
      <div className="rounded-lg border border-slate-200 p-4 space-y-3">
        <label className="block text-sm font-medium text-slate-800">
          Reason for using the app instead of the machine <span className="text-rose-600">*</span>
        </label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="e.g. Direct from site visit — Awal Plaza, Manama"
          data-testid="punch-reason"
          maxLength={500}
        />
        <div className="grid grid-cols-2 gap-3 pt-1">
          <button
            onClick={() => submit('in')}
            disabled={busy}
            className="w-full py-3 rounded-lg bg-emerald-600 text-white font-semibold text-base hover:bg-emerald-700 disabled:opacity-60 shadow-sm"
            data-testid="check-in-btn"
          >
            ✅ Check In
          </button>
          <button
            onClick={() => submit('out')}
            disabled={busy}
            className="w-full py-3 rounded-lg bg-rose-600 text-white font-semibold text-base hover:bg-rose-700 disabled:opacity-60 shadow-sm"
            data-testid="check-out-btn"
          >
            🏁 Check Out
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          role="alert"
          className={`text-sm rounded px-3 py-2 border ${
            toast.kind === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-rose-50 text-rose-800 border-rose-200'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Recent punches */}
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-2">Recent punches (last 14 days)</div>
        <div className="border rounded-lg divide-y" data-testid="punch-history">
          {history.length === 0 && (
            <div className="px-3 py-4 text-sm text-slate-500 text-center">No punches yet.</div>
          )}
          {history.map((p) => (
            <div key={p.id} className="px-3 py-2 flex items-center justify-between text-sm">
              <div>
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold mr-2 ${
                  p.action === 'in' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                }`}>{p.action === 'in' ? 'IN' : 'OUT'}</span>
                <span className="text-slate-800 font-medium">{formatDateDMY(p.punch_date)}</span>
                <span className="ml-2 text-slate-500">{new Date(p.punch_time).toLocaleTimeString()}</span>
                {p.reason && <div className="text-[11px] text-slate-500 mt-0.5 italic">"{p.reason}"</div>}
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                p.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-800'
                : p.approval_status === 'rejected' ? 'bg-rose-100 text-rose-800'
                : 'bg-amber-100 text-amber-800'
              }`}>
                {p.approval_status}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[11px] text-slate-400 pt-2">
        Logged in as <b>{user?.name || user?.email}</b>
      </div>
    </div>
  );
};

export default Attendance;
